import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type { RevenueConcentrationDTO } from "../models/revenueConcentrationDto";
import type { ReceivablesAgingDTO } from "../models/receivablesAgingDto";
import type { LastMonthRevenueDTO } from "../models/lastMonthRevenueDto";
import type { LastMonthRevenueInsightsDTO } from "../models/lastMonthRevenueInsightsDto";
import type { RevenueThisMonthDTO } from "../models/revenueThisMonthDto";
import type { RevenueThisMonthInsightsDTO } from "../models/revenueThisMonthInsightsDto";
import type { LastMonthExpenseDTO } from "../models/lastMonthExpenseDto";
import type { LastMonthExpenseInsightsDTO } from "../models/lastMonthExpenseInsightsDto";
import type { ExpenseThisMonthDTO } from "../models/expenseThisMonthDto";
import type { ExpenseThisMonthInsightsDTO } from "../models/expenseThisMonthInsightsDto";
import type { KeyMetricsRevenueDTO } from "../models/keyMetricsRevenueDto";
import type { KeyMetricsExpenseDTO } from "../models/keyMetricsExpenseDto";
import type { TwelveMonthRevenueTrendDTO } from "../models/twelveMonthRevenueTrendDto";
import type { TwelveMonthRevenueInsightsDTO } from "../models/twelveMonthRevenueInsightsDto";
import type { TwelveMonthExpenseTrendDTO } from "../models/twelveMonthExpenseTrendDto";
import type { TwelveMonthExpenseInsightsDTO } from "../models/twelveMonthExpenseInsightsDto";
import type { ExpenseBreakdownDTO } from "../models/expenseBreakdownDto";
import type { MonthAtGlanceInsightsDTO } from "../models/monthAtGlanceInsightsDto";
import type { RevenueVsExpensesInsightsDTO } from "../models/revenueVsExpensesInsightsDto";
import type { CurrentBalanceDTO } from "../models/currentBalanceDto";
import type { CurrentBalanceInsightsDTO } from "../models/currentBalanceInsightsDto";
import type { EbitdaMarginInsightsDTO } from "../models/ebitdaMarginInsightsDto";
import type { CashInOutInsightsDTO } from "../models/cashInOutInsightsDto";
import type { ClientOverviewBootstrapDTO } from "../models/clientOverviewBootstrapDto";
import type { RevenueBootstrapDTO } from "../models/revenueBootstrapDto";
import type { ExpenseBootstrapDTO } from "../models/expenseBootstrapDto";

/** Single row for Recent Revenue Activity (from API). */
export interface RecentRevenueActivityItemDTO {
  id: number;
  date: string;
  invoiceNo: string;
  customer: string;
  amount: string;
  status: "paid" | "pending" | "overdue";
  dueDate: string;
}

export interface RecentRevenueActivityPaginationDTO {
  total: number;
  limit: number;
  page: number;
  total_pages: number;
  has_more: boolean;
}

export interface RecentRevenueActivityResponseDTO {
  items: RecentRevenueActivityItemDTO[];
  pagination: RecentRevenueActivityPaginationDTO;
}

export type RecentActivityPeriod = "last7d" | "last30d" | "last90d" | "custom";
export type RecentActivityFilter = "all" | "paid" | "pending" | "overdue";

export interface GetRecentRevenueActivityParams {
  status?: RecentActivityFilter;
  period?: RecentActivityPeriod;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

/** Single row for Recent Expense Activity (from API). */
export interface RecentExpenseActivityItemDTO {
  id: number;
  date: string;
  billNo: string;
  vendor: string;
  category: string;
  amount: string;
  status: "paid" | "pending" | "overdue";
  dueDate: string;
}

export interface RecentExpenseActivityPaginationDTO {
  total: number;
  limit: number;
  page: number;
  total_pages: number;
  has_more: boolean;
}

export interface RecentExpenseActivityResponseDTO {
  items: RecentExpenseActivityItemDTO[];
  pagination: RecentExpenseActivityPaginationDTO;
}

export interface GetRecentExpenseActivityParams {
  status?: RecentActivityFilter;
  period?: RecentActivityPeriod;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

/**
 * Fetch bootstrap payload for Client Overview basic metrics.
 * GET /api/client/overview/bootstrap
 */
export async function getClientOverviewBootstrap(): Promise<ClientOverviewBootstrapDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/overview/bootstrap`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch overview bootstrap");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ClientOverviewBootstrapDTO;
  }
  throw new Error("Invalid response from overview bootstrap API");
}

/**
 * Fetch bootstrap payload for Revenue page (non-insight metrics).
 * GET /api/revenue/bootstrap
 */
export async function getRevenueBootstrap(): Promise<RevenueBootstrapDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/bootstrap`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch revenue bootstrap");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RevenueBootstrapDTO;
  }
  throw new Error("Invalid response from revenue bootstrap API");
}

/**
 * Fetch bootstrap payload for Expense page (non-insight metrics).
 * GET /api/expense/bootstrap
 */
export async function getExpenseBootstrap(): Promise<ExpenseBootstrapDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/bootstrap`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch expense bootstrap");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ExpenseBootstrapDTO;
  }
  throw new Error("Invalid response from expense bootstrap API");
}

/**
 * Fetch Current Balance (Cash & Bank) card data.
 * GET /api/client/cashAndBank/current-balance
 */
export async function getCurrentBalance(): Promise<CurrentBalanceDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/cashAndBank/current-balance`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("No data found");
    }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to load current balance");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as CurrentBalanceDTO;
  }
  throw new Error(json.message || "Failed to fetch current balance");
}

/**
 * Fetch Current Balance AI insights (3 cards). Call after current balance has loaded.
 * GET /api/client/cashAndBank/current-balance-insights
 */
export async function getCurrentBalanceInsights(): Promise<CurrentBalanceInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/cashAndBank/current-balance-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch current balance insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as CurrentBalanceInsightsDTO;
  }
  throw new Error("Invalid response from current balance insights API");
}

/**
 * Fetch Month-at-Glance LLM Insights for previous closed month.
 * GET /api/client/overview/month-at-glance-insights
 */
export async function getMonthAtGlanceInsights(): Promise<MonthAtGlanceInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/overview/month-at-glance-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch month at glance insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as MonthAtGlanceInsightsDTO;
  }
  throw new Error("Invalid response from month at glance insights API");
}

/**
 * Fetch Revenue vs Expenses AI insight for the 12-month trend.
 * Call after twelve-month-trend has loaded. GET /api/client/overview/revenue-vs-expenses-insights
 */
export async function getRevenueVsExpensesInsights(): Promise<RevenueVsExpensesInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/overview/revenue-vs-expenses-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch revenue vs expenses insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RevenueVsExpensesInsightsDTO;
  }
  throw new Error("Invalid response from revenue vs expenses insights API");
}

/**
 * Fetch EBITDA Margin AI insight for the 12-month trend.
 * Call after twelve-month-trend has loaded. GET /api/client/overview/ebitda-margin-insights
 */
export async function getEbitdaMarginInsights(): Promise<EbitdaMarginInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/overview/ebitda-margin-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch EBITDA margin insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as EbitdaMarginInsightsDTO;
  }
  throw new Error("Invalid response from EBITDA margin insights API");
}

/**
 * Fetch Cash In / Cash Out AI insight for the 12-month trend.
 * Call after twelve-month-trend has loaded. GET /api/client/overview/cash-in-out-insights
 */
export async function getCashInOutInsights(): Promise<CashInOutInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/overview/cash-in-out-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch cash in/out insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as CashInOutInsightsDTO;
  }
  throw new Error("Invalid response from cash in/out insights API");
}

export interface RevenueConcentrationApiParams {
  year?: number;
  month?: number;
}

/**
 * Fetch Revenue Concentration for Client Overview.
 * GET /api/revenue/revenue-concentration
 * Top customers by net invoiced revenue (after credit notes), unbilled journal income; remainder as Others. No companyId.
 */
export async function getRevenueConcentration(
  params?: RevenueConcentrationApiParams,
): Promise<RevenueConcentrationDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params?.year != null) searchParams.set("year", String(params.year));
  if (params?.month != null) searchParams.set("month", String(params.month));
  const query = searchParams.toString();
  const url = `${API_BASE_URL}/api/revenue/revenue-concentration${query ? `?${query}` : ""}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch revenue concentration");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RevenueConcentrationDTO;
  }
  throw new Error("Invalid response from revenue concentration API");
}

/**
 * Fetch Receivables Aging for Client Overview.
 * GET /api/revenue/receivables-aging
 * Outstanding invoices bucketed by days overdue (0-30, 31-60, 61-90, 90+d). No companyId.
 */
export async function getReceivablesAging(): Promise<ReceivablesAgingDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/receivables-aging`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch receivables aging");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ReceivablesAgingDTO;
  }
  throw new Error("Invalid response from receivables aging API");
}

/**
 * Fetch Last Month Revenue for Revenue page card.
 * GET /api/revenue/last-month
 * No query parameters; backend uses effective date and default company.
 */
export async function getLastMonthRevenue(): Promise<LastMonthRevenueDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/last-month`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch last month revenue");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as LastMonthRevenueDTO;
  }
  throw new Error("Invalid response from last month revenue API");
}

/**
 * Fetch Last Month Revenue AI insights (3 cards). Call after last-month revenue has loaded.
 * GET /api/revenue/last-month-insights
 */
export async function getLastMonthRevenueInsights(): Promise<LastMonthRevenueInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/last-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch last month revenue insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as LastMonthRevenueInsightsDTO;
  }
  throw new Error("Invalid response from last month revenue insights API");
}

/**
 * Fetch 12-Month Revenue Trend AI insights (3 cards). Call after twelve-month-trend has loaded.
 * GET /api/revenue/twelve-month-insights
 */
export async function getTwelveMonthRevenueInsights(): Promise<TwelveMonthRevenueInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/twelve-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || "Failed to fetch 12-month revenue insights",
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as TwelveMonthRevenueInsightsDTO;
  }
  throw new Error("Invalid response from 12-month revenue insights API");
}

/**
 * Fetch Revenue This Week for Revenue page card (collected revenue in current week + week-in-month).
 * GET /api/revenue/this-month
 */
export async function getRevenueThisMonth(): Promise<RevenueThisMonthDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/this-month`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch revenue this week");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RevenueThisMonthDTO;
  }
  throw new Error("Invalid response from revenue this week API");
}

/**
 * Fetch Revenue This Month insights for Revenue page panel.
 * GET /api/revenue/this-month-insights
 */
export async function getRevenueThisMonthInsights(): Promise<RevenueThisMonthInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/this-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch revenue this month insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RevenueThisMonthInsightsDTO;
  }
  throw new Error("Invalid response from revenue this month insights API");
}

/**
 * Fetch Last Month Expense for Expense page card (bills-based).
 * GET /api/expense/last-month
 */
export async function getLastMonthExpense(): Promise<LastMonthExpenseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/last-month`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch last month expense");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as LastMonthExpenseDTO;
  }
  throw new Error("Invalid response from last month expense API");
}

/**
 * Fetch Last Month Expense AI insights (3 cards). Call after last-month expense has loaded.
 * GET /api/expense/last-month-insights
 */
export async function getLastMonthExpenseInsights(): Promise<LastMonthExpenseInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/last-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch last month expense insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as LastMonthExpenseInsightsDTO;
  }
  throw new Error("Invalid response from last month expense insights API");
}

/**
 * Fetch Expense This Week for Expense page card (bills paid in current week).
 * GET /api/expense/this-month
 */
export async function getExpenseThisMonth(): Promise<ExpenseThisMonthDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/this-month`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch expense this week");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ExpenseThisMonthDTO;
  }
  throw new Error("Invalid response from expense this week API");
}

/**
 * Fetch Expense This Month insights for Expense page panel (bills-based).
 * GET /api/expense/this-month-insights
 */
export async function getExpenseThisMonthInsights(): Promise<ExpenseThisMonthInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/this-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch expense this month insights");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ExpenseThisMonthInsightsDTO;
  }
  throw new Error("Invalid response from expense this month insights API");
}

/**
 * Fetch Key Metrics for Revenue page (YTD revenue, collection rate, expense ÷ revenue).
 * GET /api/revenue/key-metrics
 */
export async function getKeyMetricsRevenue(): Promise<KeyMetricsRevenueDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/key-metrics`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch key metrics");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as KeyMetricsRevenueDTO;
  }
  throw new Error("Invalid response from key metrics API");
}

/**
 * Fetch Key Metrics for Expense page (YTD expense, recurring spend, expense ÷ revenue).
 * GET /api/expense/key-metrics
 */
export async function getKeyMetricsExpense(): Promise<KeyMetricsExpenseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/key-metrics`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch expense key metrics");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as KeyMetricsExpenseDTO;
  }
  throw new Error("Invalid response from expense key metrics API");
}

/**
 * Fetch 12-Month Revenue Trend for Revenue page chart.
 * GET /api/revenue/twelve-month-trend
 * Past 12 months (excluding current); variants strong/normal by average.
 */
export async function getTwelveMonthRevenueTrend(): Promise<TwelveMonthRevenueTrendDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/revenue/twelve-month-trend`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch 12-month revenue trend");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as TwelveMonthRevenueTrendDTO;
  }
  throw new Error("Invalid response from 12-month revenue trend API");
}

/**
 * Fetch 12-Month Expense Trend for Expense page chart.
 * GET /api/expense/twelve-month-trend
 * Past 12 months (excluding current); variants within_range / elevated / spike by average and +50% threshold.
 */
export async function getTwelveMonthExpenseTrend(): Promise<TwelveMonthExpenseTrendDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/twelve-month-trend`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch 12-month expense trend");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as TwelveMonthExpenseTrendDTO;
  }
  throw new Error("Invalid response from 12-month expense trend API");
}

/**
 * Fetch 12-Month Expense Trend AI insights (3 cards). Call after twelve-month-trend has loaded.
 * GET /api/expense/twelve-month-insights
 */
export async function getTwelveMonthExpenseInsights(): Promise<TwelveMonthExpenseInsightsDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/expense/twelve-month-insights`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || "Failed to fetch 12-month expense insights",
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as TwelveMonthExpenseInsightsDTO;
  }
  throw new Error("Invalid response from 12-month expense insights API");
}

export type ExpenseBreakdownTypeParam = "vendor" | "category";
export type ExpenseBreakdownTimeframeParam = "ytd" | "last_month";

/**
 * Fetch Expense Breakdown (top 10 by vendor or category, selected timeframe).
 * GET /api/expense/breakdown?type=vendor|category&timeframe=ytd|last_month
 */
export async function getExpenseBreakdown(
  type: ExpenseBreakdownTypeParam = "vendor",
  timeframe: ExpenseBreakdownTimeframeParam = "ytd",
): Promise<ExpenseBreakdownDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  searchParams.set("type", type);
  searchParams.set("timeframe", timeframe);
  const url = `${API_BASE_URL}/api/expense/breakdown?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch expense breakdown");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ExpenseBreakdownDTO;
  }
  throw new Error("Invalid response from expense breakdown API");
}

/**
 * Fetch Recent Revenue Activity (paginated invoices by date range and status).
 * GET /api/revenue/recent-activity?status=&period=&date_from=&date_to=&page=&limit=
 */
export async function getRecentRevenueActivity(
  params: GetRecentRevenueActivityParams,
): Promise<RecentRevenueActivityResponseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params.status != null) searchParams.set("status", params.status);
  if (params.period != null) searchParams.set("period", params.period);
  if (params.date_from != null) searchParams.set("date_from", params.date_from);
  if (params.date_to != null) searchParams.set("date_to", params.date_to);
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.limit != null) searchParams.set("limit", String(params.limit));

  const url = `${API_BASE_URL}/api/revenue/recent-activity?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch recent revenue activity");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RecentRevenueActivityResponseDTO;
  }
  throw new Error("Invalid response from recent revenue activity API");
}

/**
 * Fetch Recent Expense Activity (paginated bills by date range and status).
 * GET /api/expense/recent-activity?status=&period=&date_from=&date_to=&page=&limit=
 */
export async function getRecentExpenseActivity(
  params: GetRecentExpenseActivityParams,
): Promise<RecentExpenseActivityResponseDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const searchParams = new URLSearchParams();
  if (params.status != null) searchParams.set("status", params.status);
  if (params.period != null) searchParams.set("period", params.period);
  if (params.date_from != null) searchParams.set("date_from", params.date_from);
  if (params.date_to != null) searchParams.set("date_to", params.date_to);
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.limit != null) searchParams.set("limit", String(params.limit));

  const url = `${API_BASE_URL}/api/expense/recent-activity?${searchParams.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch recent expense activity");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RecentExpenseActivityResponseDTO;
  }
  throw new Error("Invalid response from recent expense activity API");
}
